import React, { FC, useEffect, useState } from 'react';
import Blinder from '../../../../../common/blinder/blinder';
import Button from '../../../../../common/button/button';
import DomainSensitiveLink from '../../../../../common/domain-sensitive-link/domain-sensitive-link';
import LinkButton from '../../../../../common/link-button/link-button';
import { BUTTON_VARIANTS } from '../../../../../constants/button-variants';
import { LINK_THEMES } from '../../../../../constants/link-themes';
import Routes from '../../../../../constants/routes';
import { SCHOOL_TYPES } from '../../../../../constants/school-types';
import UserIcon from '../../../../../images/noun-image.svg';
import translation from '../../../../../languages';
import headerSchoolMenu from '../../../../../services/school/header-school-menu';
import cn from '../../../../../utils/classnames';
import imagePathCreator from '../../../../../utils/image-path-creator';
import { landingLink } from '../../../../../utils/landing-link';
import withInternalPath from '../../../../../utils/url/with-internal-path';
import './school-menu.scss';

type SchoolMenuProps = TranslationProps & {
    type: SCHOOL_TYPES;
};

const SchoolMenu: FC<SchoolMenuProps> = ({ type, t }) => {
    const [menuFetched, setMenuFetched] = useState(false);

    useEffect(() => {
        headerSchoolMenu.fetch(() => {
            setMenuFetched(true);
        });
    }, []);

    const taxonomyBlock = (type: SCHOOL_TYPES) => {
        const list = headerSchoolMenu.taxonomy.getListByType('regrouped', type);

        return (
            <div className='d-flex'>
                {list.map((group, i) => (
                    <div className='taxonomy-column' key={`array-index-${String(i)}`}>
                        {group.map((category, j) => (
                            <div className='category' key={`array-index-${String(j)}`}>
                                {category.type !== 'seller' && (
                                    <div className='title'>
                                        <DomainSensitiveLink
                                            href={withInternalPath(landingLink({ category, type }), `menu_${type}`)}
                                        >
                                            {category.title}
                                        </DomainSensitiveLink>
                                    </div>
                                )}
                                {category.children?.map((subcategory, k) => (
                                    <div className='subcategory-title' key={`array-index-${String(k)}`}>
                                        <LinkButton
                                            // className='jc-start text-left'
                                            href={withInternalPath(
                                                landingLink({ category: subcategory, type }),
                                                `menu_${type}`
                                            )}
                                            text={subcategory.title}
                                            theme={LINK_THEMES.TINY}
                                        />
                                    </div>
                                ))}
                                <div className={`more ${!category.id ? 'd-none' : ''}`}>
                                    <LinkButton
                                        arrow
                                        // className='jc-start text-left'
                                        href={withInternalPath(landingLink({ category, type }), `menu_${type}`)}
                                        text={t('header.schoolMenu.more')}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
                <div className='vertical-separator' />
            </div>
        );
    };

    const filtersBlock = (type: SCHOOL_TYPES) => {
        const { themes, classes } = headerSchoolMenu;

        return (
            <div>
                <div className='title'>
                    <b>{t('header.schoolMenu.favoriteSubject')}</b>
                </div>
                <div className='taxonomy-links-wrapper'>
                    {themes[type].map((filter) => (
                        <div className='taxonomy-link' key={filter.title}>
                            <DomainSensitiveLink href={withInternalPath(filter.link, type)}>
                                <Button className='w-100' variant={BUTTON_VARIANTS.TAG}>
                                    {filter.title}
                                </Button>
                            </DomainSensitiveLink>
                        </div>
                    ))}
                </div>
                <div className='title'>{t('header.schoolMenu.classes')}</div>
                <div className='filter-links-wrapper'>
                    {classes[type].map((filter) => (
                        <div className='class-filter' key={filter.title}>
                            <DomainSensitiveLink href={withInternalPath(filter.link, type)}>
                                <Button className='w-100' variant={BUTTON_VARIANTS.TAG}>
                                    {filter.title}
                                </Button>
                            </DomainSensitiveLink>
                        </div>
                    ))}
                </div>
                <div className='vertical-separator' />
            </div>
        );
    };

    const authorsBlock = (type: SCHOOL_TYPES) => {
        const { authors } = headerSchoolMenu;

        return (
            <div>
                <div className='title'>
                    <b>{t('header.schoolMenu.favoriteAuthor')}</b>
                </div>
                {authors[type].map((author) => (
                    <div className='author-block' key={author.user_id}>
                        <DomainSensitiveLink
                            href={
                                author.type === 'seller'
                                    ? withInternalPath(Routes.SELLER.path, 'ra')
                                    : withInternalPath(
                                          `${Routes.AUTHOR_MAIN.prefix}/${author.user_id}/${author.slug}`,
                                          type
                                      )
                            }
                        >
                            <div
                                className={cn('author-avatar', { 'no-image': !author.image_path })}
                                style={{
                                    backgroundImage: `url(${imagePathCreator.resizedImg(
                                        { url: author.image_path || UserIcon },
                                        { h: 33, q: 70, w: 33 }
                                    )})`
                                }}
                            />
                            <div
                                className={cn('author-name d-flex align-items-center', {
                                    'seller-author': author.type === 'seller'
                                })}
                            >
                                <span className={cn({ 'yellow-text': author.type === 'seller' })}>
                                    {author.public_name?.length > 30
                                        ? `${author.public_name.slice(0, 25)}...`
                                        : author.public_name}
                                </span>
                            </div>
                        </DomainSensitiveLink>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className='school-menu position-relative d-flex'>
            {menuFetched ? (
                <>
                    <div className='block taxonomy-block'>{taxonomyBlock(type)}</div>
                    <div className='block filters-block'>{filtersBlock(type)}</div>
                    <div className='block authors-block'>{authorsBlock(type)}</div>
                </>
            ) : (
                <Blinder />
            )}
        </div>
    );
};

const translate = translation('header');

export default translate(SchoolMenu);
